import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    DocumentCheckIcon,
    ArrowDownTrayIcon,
    StarIcon,
    MagnifyingGlassIcon,
    ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import API_URL from '../config/apiConfig';

const ReportsPage = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRating, setFilterRating] = useState('all'); // all, rated, unrated, 1-5
    const [hoverStates, setHoverStates] = useState({}); // { reportId: hoveredStar }

    const getAuthHeaders = () => ({
        headers: { 'auth-token': localStorage.getItem('auth-token') }
    });

    const fetchReports = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/admin/reports`, getAuthHeaders());
            setReports(response.data);
        } catch (error) {
            console.error('Erreur chargement rapports:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReports(); }, []);

    const handleRate = async (reportId, rating) => {
        try {
            await axios.put(`${API_URL}/admin/reports/${reportId}/rate`, { rating }, getAuthHeaders());
            setReports(reports.map(r => r.id === reportId ? { ...r, rating } : r));
        } catch (error) {
            console.error('Erreur notation:', error);
        }
    };

    const setHover = (reportId, star) => {
        setHoverStates(prev => ({ ...prev, [reportId]: star }));
    };

    // Filtrage
    const filteredReports = reports.filter(report => {
        const matchSearch =
            (report.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (report.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (report.user?.email || '').toLowerCase().includes(searchTerm.toLowerCase());

        if (filterRating === 'all') return matchSearch;
        if (filterRating === 'rated') return matchSearch && report.rating;
        if (filterRating === 'unrated') return matchSearch && !report.rating;
        return matchSearch && report.rating === parseInt(filterRating);
    });

    const avgRating = reports.filter(r => r.rating).length > 0
        ? (reports.filter(r => r.rating).reduce((sum, r) => sum + r.rating, 0) / reports.filter(r => r.rating).length).toFixed(1)
        : '-';

    const timeAgo = (date) => {
        if (!date) return '-';
        const diff = Date.now() - new Date(date).getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return "A l'instant";
        if (minutes < 60) return `Il y a ${minutes}min`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `Il y a ${hours}h`;
        const days = Math.floor(hours / 24);
        if (days < 30) return `Il y a ${days}j`;
        return new Date(date).toLocaleDateString('fr-FR');
    };

    const StarRating = ({ reportId, currentRating }) => {
        const hover = hoverStates[reportId] || 0;
        return (
            <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => {
                    const filled = (hover || currentRating || 0) >= star;
                    return (
                        <button
                            key={star}
                            onClick={() => handleRate(reportId, star)}
                            onMouseEnter={() => setHover(reportId, star)}
                            onMouseLeave={() => setHover(reportId, 0)}
                            className="focus:outline-none transition-transform hover:scale-125"
                        >
                            {filled ? (
                                <StarSolid className="w-5 h-5 text-[#Ceaa5b]" />
                            ) : (
                                <StarIcon className="w-5 h-5 text-gray-300" />
                            )}
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="p-6 max-w-7xl mx-auto animate-fade-in">

            {/* EN-TÊTE */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#1c2143]">Pré-rapports</h1>
                    <p className="text-gray-500 text-sm mt-1">Consultez et notez les pré-rapports générés par les experts.</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Stats rapides */}
                    <div className="flex gap-3">
                        <div className="bg-white rounded-lg px-4 py-2 border border-gray-100 shadow-sm text-center">
                            <p className="text-xs text-gray-400">Total</p>
                            <p className="text-lg font-bold text-[#1c2143]">{reports.length}</p>
                        </div>
                        <div className="bg-white rounded-lg px-4 py-2 border border-gray-100 shadow-sm text-center">
                            <p className="text-xs text-gray-400">Notés</p>
                            <p className="text-lg font-bold text-emerald-600">{reports.filter(r => r.rating).length}</p>
                        </div>
                        <div className="bg-white rounded-lg px-4 py-2 border border-gray-100 shadow-sm text-center">
                            <p className="text-xs text-gray-400">Moyenne</p>
                            <p className="text-lg font-bold text-[#Ceaa5b]">{avgRating}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* FILTRES */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-100 gap-3">
                <div className="relative w-full max-w-md">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#Ceaa5b] focus:ring-1 focus:ring-[#Ceaa5b] transition-colors sm:text-sm"
                        placeholder="Rechercher par titre, expert..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Filtrer :</span>
                    {[
                        { value: 'all', label: 'Tous' },
                        { value: 'unrated', label: 'Non notés' },
                        { value: 'rated', label: 'Notés' },
                    ].map(f => (
                        <button
                            key={f.value}
                            onClick={() => setFilterRating(f.value)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                filterRating === f.value
                                    ? 'bg-[#1c2143] text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    {[1, 2, 3, 4, 5].map(n => (
                        <button
                            key={n}
                            onClick={() => setFilterRating(filterRating === String(n) ? 'all' : String(n))}
                            className={`px-2 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-0.5 ${
                                filterRating === String(n)
                                    ? 'bg-[#Ceaa5b] text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {n}<StarSolid className="w-3 h-3" />
                        </button>
                    ))}
                </div>
            </div>

            {/* TABLEAU */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-[#1c2143] text-white">
                            <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Pré-rapport</th>
                                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">Expert</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Date</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Note</th>
                                <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-10 text-center">
                                        <div className="flex justify-center items-center text-[#1c2143]">
                                            <ArrowPathIcon className="w-6 h-6 animate-spin mr-2" />
                                            Chargement...
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredReports.length > 0 ? (
                                filteredReports.map((report) => (
                                    <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                                        {/* TITRE */}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <DocumentCheckIcon className="w-5 h-5 text-[#Ceaa5b] flex-shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="font-medium text-gray-900 truncate max-w-xs">{report.title || 'Sans titre'}</p>
                                                    <p className="text-xs text-gray-400">ID #{report.id}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* EXPERT */}
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <div className="w-7 h-7 rounded-full bg-[#1c2143] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                                    {(report.user?.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{report.user?.name || '-'}</p>
                                                    <p className="text-xs text-gray-400">{report.user?.email || ''}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* DATE */}
                                        <td className="px-6 py-4 text-center">
                                            <span className="text-sm text-gray-500">{timeAgo(report.updatedAt)}</span>
                                        </td>

                                        {/* NOTE */}
                                        <td className="px-6 py-4">
                                            <div className="flex justify-center">
                                                <StarRating reportId={report.id} currentRating={report.rating} />
                                            </div>
                                        </td>

                                        {/* ACTIONS */}
                                        <td className="px-6 py-4 text-center">
                                            {report.pdfUrl && (
                                                <a
                                                    href={report.pdfUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1c2143] text-white rounded-lg hover:bg-[#2a3055] transition-all text-xs font-medium"
                                                >
                                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                                    Télécharger
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        Aucun pré-rapport trouvé.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ReportsPage;
